import { Field, FormikConfig, FormikProps, FormikValues } from 'formik';
import * as React from 'react';
import { isEmpty } from 'lodash';
import DynamicForm, { DynamicField } from './DynamicForm';

type Props<Values extends FormikValues = FormikValues> = {
  initialValues: Values;
  fields: DynamicField[];
  ignoreResetFields: Array<string>;
  bottomFields?: DynamicField[];
  [k: string]: any;
} & FormikConfig<Values>;

function ProgressiveForm<Values extends FormikValues>(
  {
    initialValues,
    fields,
    ignoreResetFields,
    onSubmit,
    bottomFields,
    ...props
  }: Props<Values>,
  ref: React.ForwardedRef<FormikProps<Values>>,
) {
  const isVisible = (values, name) => {
    const fieldIndex = fields.findIndex(field => field.name === name);

    if (fieldIndex === 0) {
      return true;
    }

    let ret = true;
    for (let i = 0; i < fieldIndex; i++) {
      ret = ret && !isEmpty(values[fields[i].name]);
    }

    return ret;
  };

  return (
    <DynamicForm
      bottomFields={bottomFields}
      initialValues={initialValues}
      onSubmit={onSubmit}
      fields={fields}
      ref={ref}
      fieldRender={(form, { name, label, component }, fieldIndex) => {
        const { values }: any = form;
        // TODO(Greg): Refactor it, it does not related about ProgressiveForm
        const isLastStep = values?.consultationReasonName && values?.type;
        const number = isLastStep ? fields.length + 1 : fieldIndex + 1;

        if (!isVisible(form.values, name)) {
          return null;
        }

        return (
          <div key={name} className={`rdv__${name}`}>
            <div
              className={`label ${
                !isLastStep && label === bottomFields?.[0]?.label
                  ? 'is-disabled'
                  : ''
              }`}
            >
              <span className="number">{isLastStep && number}</span>
              <label htmlFor={name}>{label}</label>
            </div>
            <Field
              id={name}
              name={name}
              component={component}
              onReset={() => {
                const resetValues = fields.reduce(
                  (curr, field, index) => ({
                    ...curr,
                    ...(index > fieldIndex &&
                    !ignoreResetFields?.includes(field.name)
                      ? { [field.name]: form.initialValues[field.name] }
                      : {}),
                  }),
                  {},
                );
                if (Object.keys(resetValues).length > 0)
                  form.setValues(
                    prevValues => ({
                      ...prevValues,
                      ...resetValues,
                    }),
                    false,
                  );
              }}
              {...props}
            />
          </div>
        );
      }}
    />
  );
}

export default React.forwardRef(ProgressiveForm) as <
  Values extends FormikValues
>(
  props: Props<Values> & { ref?: React.ForwardedRef<FormikProps<Values>> },
) => ReturnType<typeof ProgressiveForm>;
